/**
 * <region-widget> — header country/currency switcher. A `USD ⌄` trigger that slides a small
 * "Country / Currency" card down from behind the header. See snippets/region-widget.liquid.
 *
 * The native <select name="country_code"> (sr-only) is the source of truth. Clicking a region row
 * sets the select and submits the localization form → Shopify 302-reloads in the new currency.
 * The current region's row is a NO-OP (you're already there).
 *
 * Motion is pure CSS (the `.is-open` class on the root drives a transform). This element only wires
 * open/close (trigger, X, click-outside, Escape) and the region choice.
 *
 * Choosing a region writes `ss-region-choice` — the SAME key region-banner.js reads — so switching
 * here also stops the geolocation banner from ever nudging again.
 */
(function () {
  if (customElements.get('region-widget')) return;

  var CHOICE_KEY = 'ss-region-choice'; // shared with region-banner.js / region-dropdown.js

  class RegionWidget extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.region-widget-form');
      this.select = this.querySelector('[data-region-select]');
      this.trigger = this.querySelector('[data-widget-trigger]');
      this.panel = this.querySelector('[data-widget-panel]');
      this.line = this.querySelector('.region-widget__line');
      if (!this.form || !this.select || !this.trigger || !this.panel) return;

      this._onTrigger = this.onTrigger.bind(this);
      this._onOption = this.onOption.bind(this);
      this._onClose = function () { this.toggle(false); this.trigger.focus(); }.bind(this);
      this._onDocClick = this.onDocClick.bind(this);
      this._onKeydown = this.onKeydown.bind(this);
      this._onSubmit = this.recordChoice.bind(this);
      this._onLineEnter = this.onLineEnter.bind(this);
      this._onLineLeave = this.onLineLeave.bind(this);
      this._onLineEnd = this.onLineEnd.bind(this);

      this.trigger.addEventListener('click', this._onTrigger);
      this.panel.addEventListener('click', this._onOption);
      var closeEls = this.querySelectorAll('[data-widget-close]');
      for (var i = 0; i < closeEls.length; i++) closeEls[i].addEventListener('click', this._onClose);
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKeydown);
      this.form.addEventListener('submit', this._onSubmit);

      // Underline wipe: enter from the left, exit to the right, reset to the left to loop.
      this.trigger.addEventListener('mouseenter', this._onLineEnter);
      this.trigger.addEventListener('mouseleave', this._onLineLeave);
      if (this.line) this.line.addEventListener('transitionend', this._onLineEnd);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKeydown);
      if (this.form) this.form.removeEventListener('submit', this._onSubmit);
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    toggle(force) {
      var open = force != null ? force : !this.isOpen;
      this.classList.toggle('is-open', open);
      this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      // The underline holds IN while the panel is open, then wipes OUT when it closes (X/outside/ESC).
      if (open) this.lineIn();
      else this.wipeOut();
    }

    onTrigger(e) {
      e.preventDefault();
      this.toggle();
    }

    onDocClick(e) {
      if (this.isOpen && !this.contains(e.target)) this.toggle(false);
    }

    onKeydown(e) {
      if (e.key === 'Escape' && this.isOpen) {
        this.toggle(false);
        this.trigger.focus();
      }
    }

    /* ---------------- Underline wipe ---------------- */
    // Hover only drives the line while CLOSED. While open the bar is held in until the panel closes,
    // so moving the pointer off the trigger must NOT wipe it out — toggle() handles that on close.
    onLineEnter() {
      if (this.isOpen) return;
      this.lineIn();
    }

    onLineLeave() {
      if (this.isOpen) return;
      this.wipeOut();
    }

    lineIn() {
      this.trigger.classList.remove('is-line-out');
      this.trigger.classList.add('is-line-in');
    }

    // Exit the bar to the right — but only if it's currently in. From the parked (off-left) state,
    // adding is-line-out would instead sweep the bar all the way across, which we don't want.
    wipeOut() {
      if (!this.trigger.classList.contains('is-line-in')) return;
      this.trigger.classList.remove('is-line-in');
      this.trigger.classList.add('is-line-out');
    }

    // After the exit finishes, drop the class → the bar snaps back off-left (rest has no transition),
    // ready for the next wipe. Ignore the enter transition's end.
    onLineEnd(e) {
      if (e.propertyName !== 'transform') return;
      if (this.trigger.classList.contains('is-line-out')) {
        this.trigger.classList.remove('is-line-out');
      }
    }

    onOption(e) {
      var option = e.target.closest('[data-widget-option]');
      if (!option || !this.panel.contains(option)) return;

      var value = option.getAttribute('data-value');
      if (!value || value === this.select.value) return; // current region → no-op

      this.select.value = value;
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(); // fires 'submit' → recordChoice() → Shopify reloads
      } else {
        this.recordChoice(); // form.submit() does NOT fire 'submit', so record it ourselves
        this.form.submit();
      }
    }

    // Persist the explicit choice so the geolocation banner won't nudge again. Survives the reload.
    recordChoice() {
      try {
        window.localStorage.setItem(
          CHOICE_KEY,
          JSON.stringify({ iso: (this.select.value || '').toUpperCase(), ts: Date.now() })
        );
      } catch (e) {
        /* ignore storage failures */
      }
    }
  }

  customElements.define('region-widget', RegionWidget);
})();
